import { useState, useCallback } from 'react';
import type { SelectionInfo } from './TipTapEditor';
import { useI18n } from '../../../../../i18n';
import {
  viaGenerar,
  viaCorregir,
  viaAuditar,
  viaTraducir,
  chatVIA,
} from '../../../../shared/services/api';

// ─── VIA permissions by role ───
const PERMISOS_VIA: Record<string, string[]> = {
  CEO: [
    'GENERAR',
    'AUDITAR',
    'TRADUCIR',
    'MEJORAR',
    'AMPLIAR',
    'RESUMIR',
    'VERIFICAR_TERMINOLOGIA',
    'REFORMULAR',
    'DEFINIR_TERMINO',
  ],
  CTO: [
    'GENERAR',
    'AUDITAR',
    'TRADUCIR',
    'MEJORAR',
    'AMPLIAR',
    'RESUMIR',
    'VERIFICAR_TERMINOLOGIA',
    'REFORMULAR',
    'DEFINIR_TERMINO',
  ],
  COO: [
    'GENERAR',
    'AUDITAR',
    'TRADUCIR',
    'MEJORAR',
    'AMPLIAR',
    'RESUMIR',
    'VERIFICAR_TERMINOLOGIA',
    'REFORMULAR',
    'DEFINIR_TERMINO',
  ],
  EDITOR: [
    'MEJORAR',
    'AMPLIAR',
    'RESUMIR',
    'VERIFICAR_TERMINOLOGIA',
    'REFORMULAR',
    'DEFINIR_TERMINO',
  ],
  VIEWER: [],
};

interface QuickAction {
  id: string;
  label: string;
  permiso: string;
  icon: string;
  context: ('none' | 'section' | 'text')[];
}

const QUICK_ACTIONS: QuickAction[] = [
  { id: 'generar', label: 'Generar contenido', permiso: 'GENERAR', icon: '⚡', context: ['none'] },
  { id: 'auditar', label: 'Auditar documento', permiso: 'AUDITAR', icon: '🔍', context: ['none'] },
  { id: 'traducir', label: 'Traducir', permiso: 'TRADUCIR', icon: '🌐', context: ['none'] },
  { id: 'mejorar', label: 'Mejorar seccion', permiso: 'MEJORAR', icon: '✨', context: ['section'] },
  { id: 'ampliar', label: 'Ampliar seccion', permiso: 'AMPLIAR', icon: '📝', context: ['section'] },
  { id: 'resumir', label: 'Resumir seccion', permiso: 'RESUMIR', icon: '📋', context: ['section'] },
  {
    id: 'verificar',
    label: 'Verificar terminologia',
    permiso: 'VERIFICAR_TERMINOLOGIA',
    icon: '✅',
    context: ['section'],
  },
  {
    id: 'reformular',
    label: 'Reformular texto',
    permiso: 'REFORMULAR',
    icon: '🔄',
    context: ['text'],
  },
  {
    id: 'definir',
    label: 'Definir termino',
    permiso: 'DEFINIR_TERMINO',
    icon: '📖',
    context: ['text'],
  },
];

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface VIAPanelProps {
  open: boolean;
  onToggle: () => void;
  codigoDocumento: string;
  versionId: string;
  selectionInfo: SelectionInfo | null;
  rolUsuario: string;
  onSugerenciasGeneradas: () => void;
  mode?: 'read' | 'edit';
}

// Acciones que requieren modo edición
const WRITE_ACTIONS = new Set(['generar', 'mejorar', 'ampliar', 'resumir', 'reformular']);

export function VIAPanel({
  open,
  onToggle,
  codigoDocumento,
  versionId,
  selectionInfo,
  rolUsuario,
  onSugerenciasGeneradas,
  mode = 'edit',
}: VIAPanelProps) {
  const { t } = useI18n();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // Determine which actions are available
  const userPermisos = PERMISOS_VIA[rolUsuario] || PERMISOS_VIA.VIEWER || [];
  const selectionType = selectionInfo?.type || 'none';

  const availableActions = QUICK_ACTIONS.filter((action) => {
    if (!userPermisos.includes(action.permiso)) return false;
    if (mode === 'read' && WRITE_ACTIONS.has(action.id)) return false;
    // Map selection type to action context
    if (selectionType === 'text') return action.context.includes('text');
    if (selectionType === 'section') return action.context.includes('section');
    return action.context.includes('none');
  });

  // Ejecutar acción V·IA contra el backend real
  const executeAction = useCallback(
    async (actionId: string) => {
      setLoading(true);
      try {
        // Mapeo de acciones a funciones de API
        let result: any;
        switch (actionId) {
          case 'generar':
            result = await viaGenerar({ codigoDocumento, versionId, tipo: 'seccion' });
            break;
          case 'ampliar':
            result = await viaGenerar({ codigoDocumento, versionId, tipo: 'ampliar' });
            break;
          case 'mejorar':
            result = await viaCorregir({
              codigoDocumento,
              instruccion: 'Mejora estilo y precision',
            });
            break;
          case 'resumir':
            result = await viaCorregir({
              codigoDocumento,
              instruccion: 'Resume manteniendo puntos clave',
            });
            break;
          case 'auditar':
            result = await viaAuditar(codigoDocumento);
            break;
          case 'traducir':
            result = await viaTraducir({ codigoDocumento, idiomaDestino: 'en' });
            break;
          case 'verificar':
            result = await viaCorregir({
              codigoDocumento,
              instruccion: 'Verifica terminologia del sistema',
            });
            break;
          case 'reformular':
            result = await viaCorregir({
              codigoDocumento,
              instruccion: 'Reformula el texto seleccionado',
            });
            break;
          case 'definir':
            result = await viaGenerar({ codigoDocumento, versionId, tipo: 'definiciones' });
            break;
          default:
            throw new Error(`Accion desconocida: ${actionId}`);
        }

        // Extraer contenido útil de la respuesta
        const content =
          result?.data?.contenido ||
          result?.data?.respuesta ||
          result?.data?.hallazgos?.map((h: any) => `- ${h.seccion}: ${h.hallazgo}`).join('\n') ||
          JSON.stringify(result?.data);

        setChatMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: content || t.via.chat.accionCompletada,
            timestamp: new Date(),
          },
        ]);
        onSugerenciasGeneradas();
      } catch {
        setChatMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: t.via.chat.errorConexion,
            timestamp: new Date(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [codigoDocumento, versionId, onSugerenciasGeneradas, t],
  );

  // Enviar mensaje al chat V·IA via backend
  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setLoading(true);

    try {
      const historial = chatMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await chatVIA(text, historial);
      const content = res?.data?.respuesta || res?.data?.response || t.via.chat.noDisponible;

      setChatMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content,
          timestamp: new Date(),
        },
      ]);
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: t.via.chat.errorConexion,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [inputValue, chatMessages, t]);

  // Collapsed state
  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 px-1.5 py-4 bg-[#1F3864] text-white rounded-l-lg shadow-lg hover:bg-[#2F5496] transition-colors"
        style={{ writingMode: 'vertical-lr' }}
        title={t.via.abrirPanel}
      >
        V·IA
      </button>
    );
  }

  return (
    <div className="w-[300px] h-full flex flex-col border-l border-gray-200 bg-white shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1F3864] text-white shrink-0">
        <div>
          <h3 className="text-sm font-bold tracking-wide">{t.via.titulo}</h3>
          <p className="text-[10px] text-blue-200 mt-0.5">{t.via.inteligenciaNormativa}</p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="p-1 hover:bg-white/10 rounded transition-colors text-white/80 hover:text-white"
          title={t.via.cerrarPanel}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Context indicator */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 shrink-0">
        <p className="text-[11px] text-gray-500">
          {t.via.contexto}:{' '}
          <span className="font-medium text-gray-700">
            {selectionType === 'text'
              ? `Texto: "${selectionInfo?.selectedText?.slice(0, 30)}..."`
              : selectionType === 'section'
                ? `Seccion: ${selectionInfo?.seccionId || 'actual'}`
                : t.via.documentoCompleto}
          </span>
        </p>
      </div>

      {/* Quick actions */}
      <div className="px-3 py-3 border-b border-gray-200 shrink-0">
        <p className="text-[10px] font-semibold uppercase text-gray-400 mb-2 tracking-wider">
          {t.via.accionesRapidas}
        </p>
        <div className="grid grid-cols-1 gap-1.5">
          {availableActions.length > 0 ? (
            availableActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => executeAction(action.id)}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md bg-gray-50 hover:bg-[#33BCE1]/10 hover:text-[#1F3864] border border-transparent hover:border-[#33BCE1]/30 transition-all disabled:opacity-50"
              >
                <span className="text-base shrink-0">{action.icon}</span>
                <span className="truncate">{action.label}</span>
              </button>
            ))
          ) : (
            <p className="text-xs text-gray-400 italic py-2">{t.via.sinAcciones}</p>
          )}
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {chatMessages.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-8">
            <p className="font-medium mb-1">{t.via.chat.asistenteListo}</p>
            <p>{t.via.chat.seleccioneAccion}</p>
          </div>
        )}
        {chatMessages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                msg.role === 'user' ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-lg text-sm animate-pulse">
              {t.via.chat.procesando}
            </div>
          </div>
        )}
      </div>

      {/* Chat input */}
      <div className="px-3 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder={t.via.chat.placeholder}
            disabled={loading}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#33BCE1] focus:border-transparent disabled:opacity-50"
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={loading || !inputValue.trim()}
            className="px-3 py-2 bg-[#33BCE1] text-white rounded-md text-sm font-medium hover:bg-[#2AA5C8] disabled:opacity-50 transition-colors shrink-0"
          >
            {t.via.chat.enviar}
          </button>
        </div>
      </div>
    </div>
  );
}
